import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import {
  Kanban, CurrencyDollar, TrendUp, Warning, HardHat, ClockCounterClockwise,
  ShieldCheck, ClipboardText, Hammer, HourglassMedium, CheckCircle,
  Wrench, MagnifyingGlassPlus, Money, Trophy, CalendarCheck, Plus, Receipt,
  ArrowRight, Bell, X, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, apiError, downloadFile, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import SupervisorInlineActions from "@/components/SupervisorInlineActions";

const KPI = ({ icon: Icon, label, value, sub, color = "text-blue-900", testid }) => (
  <div className="kpi-card bg-white border border-slate-200 rounded-lg p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="overline-label">{label}</span>
      <Icon size={18} className={color} weight="bold" />
    </div>
    <p className="font-heading font-black text-3xl tracking-tight text-slate-900">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

const TYPE_COLORS = ["#1E3A8A", "#F59E0B", "#0F766E", "#EF4444", "#7C3AED"];

// Role → set of KPI cards to render on the dashboard. Executive roles see
// financial + safety aggregates; operational roles see their own activity.
const ROLE_VIEW = {
  admin: "executive", owner: "executive", manager: "executive", director: "executive",
  jefatura: "supervisor", supervisor: "supervisor",
  operador: "operator", mecanico: "operator", perforista: "operator", almacenista: "operator",
  viewer: "executive",
};

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [backlogAging, setBacklogAging] = useState(null);
  const [inspKpis, setInspKpis] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const view = ROLE_VIEW[user?.role] || "executive";

  const markRead = async (id) => {
    try {
      await api.post(`/me/notifications/${id}/read`);
      const r = await api.get("/me/notifications?only_unread=true");
      setNotifs(r.data);
    } catch (e) { /* silent */ void e; }
  };
  const downloadWeeklyPdf = async () => {
    try {
      await downloadFile("/me/weekly-pdf", `resumen-semanal-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
    // Personal activity is fetched for operator/supervisor views only.
    if (view !== "executive") {
      api.get("/dashboard/mine").then((r) => setMine(r.data)).catch(() => setMine({}));
    }
    // Operator KPIs (destajo, demoras, asistencias, ranking crew) — solo operador
    if (view === "operator") {
      api.get("/me/workspace").then((r) => setWorkspace(r.data)).catch(() => setWorkspace(null));
      api.get("/me/notifications?only_unread=true").then((r) => setNotifs(r.data)).catch(() => setNotifs(null));
    }
    // Backlog aging KPI: only executive + supervisor need to see this.
    if (view === "executive" || view === "supervisor") {
      api.get("/maintenance/kpis/backlog-aging")
        .then((r) => setBacklogAging(r.data))
        .catch(() => setBacklogAging(null));
      // Inspecciones KPI: SLA compliance + top inspectores
      api.get("/maintenance/inspections/kpis?days=30")
        .then((r) => setInspKpis(r.data))
        .catch(() => setInspKpis(null));
    }
  }, [view]);

  if (!data) return <p className="text-slate-500">{t("loading")}</p>;

  const costData = (data.projects || []).map((p) => ({ name: p.code, [t("budget")]: p.budget, [t("actual")]: p.actual_cost }));
  const incidentData = Object.entries(data.safety?.by_type || {}).map(([k, v]) => ({ name: t(k), value: v }));

  return (
    <div className="space-y-8">
      <div>
        <p className="overline-label">{t("dashboard")}</p>
        <h1 className="font-heading font-black text-4xl tracking-tight text-slate-900 mt-1">{t("welcomeBack")}</h1>
        {user?.name && (
          <p className="text-sm text-slate-500 mt-1" data-testid="dashboard-user-tag">
            {user.name} · <span className="uppercase tracking-widest text-xs font-bold text-slate-400">{t(`role.${user.role}`) !== `role.${user.role}` ? t(`role.${user.role}`) : user.role}</span>
          </p>
        )}
      </div>

      {view === "operator" && <OperatorView t={t} mine={mine} workspace={workspace} notifs={notifs} onMarkRead={markRead} onDownloadPdf={downloadWeeklyPdf} />}
      {view === "supervisor" && <SupervisorView t={t} data={data} mine={mine} backlogAging={backlogAging} inspKpis={inspKpis} />}
      {view === "executive" && (
        <ExecutiveView t={t} data={data} costData={costData} incidentData={incidentData} backlogAging={backlogAging} inspKpis={inspKpis} />
      )}
    </div>
  );
}

/* --- Executive: same as legacy dashboard, kept intact for compatibility --- */
function ExecutiveView({ t, data, costData, incidentData, backlogAging, inspKpis }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-active-projects" icon={Kanban} label={t("activeProjects")} value={data.projects_by_status?.active || 0} sub={`${data.projects_total || 0} ${t("projects").toLowerCase()}`} />
        <KPI testid="kpi-budget" icon={CurrencyDollar} label={t("totalBudget")} value={fmtMoney(data.total_budget || 0)} sub={`${t("spent")}: ${fmtMoney(data.total_actual || 0)}`} />
        <KPI testid="kpi-progress" icon={TrendUp} label={t("avgProgress")} value={`${data.avg_progress || 0}%`} color="text-emerald-600" />
        <KPI testid="kpi-open-incidents" icon={Warning} label={t("openIncidents")} value={data.safety?.open_incidents || 0} sub={`${data.safety?.total_incidents || 0} ${t("totalIncidents").toLowerCase()}`} color="text-red-500" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-frequency" icon={HardHat} label={t("frequencyRate")} value={data.safety?.frequency_rate || 0} sub={t("perMillionHours")} color="text-amber-500" />
        <KPI testid="kpi-severity" icon={ClockCounterClockwise} label={t("severityRate")} value={data.safety?.severity_rate || 0} sub={t("perMillionHours")} color="text-amber-500" />
        <KPI testid="kpi-days-lost" icon={Warning} label={t("daysLost")} value={data.safety?.days_lost || 0} color="text-red-500" />
        <KPI testid="kpi-compliance" icon={HardHat} label={t("avgCompliance")} value={`${data.safety?.avg_compliance || 0}%`} color="text-emerald-600" />
      </div>
      {backlogAging && <BacklogAgingBanner data={backlogAging} />}
      {inspKpis && <InspectionsKpiBanner data={inspKpis} />}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <p className="overline-label mb-4">{t("budgetVsActual")}</p>
          {costData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={costData}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey={t("budget")} fill="#1E3A8A" radius={[3, 3, 0, 0]} />
                <Bar dataKey={t("actual")} fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-20 text-center">{t("noData")}</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <p className="overline-label mb-4">{t("incidentsByType")}</p>
          {incidentData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={incidentData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={3}>
                  {incidentData.map((entry) => <Cell key={entry.name} fill={TYPE_COLORS[incidentData.indexOf(entry) % TYPE_COLORS.length]} />)}
                </Pie>
                <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-20 text-center">{t("noData")}</p>}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg">
        <p className="overline-label px-6 pt-6">{t("projectPortfolio")}</p>
        <table className="w-full data-table mt-2">
          <thead>
            <tr><th>{t("code")}</th><th>{t("name")}</th><th>{t("status")}</th><th>{t("progress")}</th><th className="text-right">{t("budget")}</th></tr>
          </thead>
          <tbody>
            {(data.projects || []).map((p) => (
              <tr key={p.id}>
                <td className="font-mono text-xs font-semibold">{p.code}</td>
                <td><Link to={`/projects/${p.id}`} className="font-medium text-blue-900 hover:underline" data-testid={`dashboard-project-link-${p.code}`}>{p.name}</Link></td>
                <td><span className="text-xs uppercase tracking-wider font-semibold text-slate-600">{t(p.status)}</span></td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-24 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-900" style={{ width: `${p.progress}%` }} />
                    </div>
                    <span className="text-xs text-slate-500">{p.progress}%</span>
                  </div>
                </td>
                <td className="text-right font-medium">{fmtMoney(p.budget)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* --- Operator: personal activity + relevant safety metrics --- */
function OperatorView({ t, mine, workspace, notifs, onMarkRead, onDownloadPdf }) {
  const m = mine || {};
  const ok = workspace?.operator_kpis;
  const inbox = notifs?.items || [];
  return (
    <>
      {/* Notifications banner */}
      {inbox.length > 0 && (
        <div className="space-y-2" data-testid="op-notif-banner">
          {inbox.slice(0, 3).map((n) => (
            <div key={n.id} className="border rounded-lg p-3 flex items-start gap-3 bg-amber-50 border-amber-300"
                  data-testid={`op-notif-${n.kind}`}>
              <Bell size={18} weight="fill" className="text-amber-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-slate-900">{n.title}</p>
                <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
              </div>
              {n.kind === "weekly_pdf_ready" && (
                <button onClick={onDownloadPdf} data-testid="op-notif-pdf-download"
                  className="text-xs font-semibold bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 rounded gap-1 inline-flex items-center">
                  <DownloadSimple size={14} weight="bold" /> Descargar PDF
                </button>
              )}
              <button onClick={() => onMarkRead(n.id)} className="text-slate-400 hover:text-slate-700"
                       data-testid={`op-notif-close-${n.id}`}>
                <X size={16} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* KPI base — Turnos / Horas / Observaciones / Charlas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="op-kpi-shifts" icon={ClockCounterClockwise} label={t("dashOpShifts")} value={m.my_shifts_this_week || 0} sub={t("dashOpShiftsHint")} />
        <KPI testid="op-kpi-hours" icon={Hammer} label={t("dashOpHours")} value={`${m.my_hours_this_week || 0} h`} sub={t("dashThisWeek")} color="text-emerald-600" />
        <KPI testid="op-kpi-observations" icon={ShieldCheck} label={t("dashOpObservations")} value={m.my_observations_this_week || 0} sub={t("dashOpObservationsHint")} color="text-amber-500" />
        <KPI testid="op-kpi-toolbox" icon={ClipboardText} label={t("dashOpToolbox")} value={m.my_toolbox_this_week || 0} sub={t("dashOpToolboxHint")} color="text-blue-900" />
      </div>

      {/* Fila 2 — Destajo, demoras, asistencias, ranking */}
      {ok && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="op-kpi-row-2">
          <KPI testid="op-kpi-destajo" icon={Money}
                label="Destajo semana" value={fmtMoney(ok.destajo_week?.gross || 0)}
                sub={`${ok.destajo_week?.concepts?.length || 0} conceptos activos`}
                color="text-emerald-700" />
          <KPI testid="op-kpi-delays" icon={HourglassMedium}
                label="Demoras semana" value={`${Math.round((ok.operations_week?.delay_minutes || 0))} min`}
                sub={`${ok.operations_week?.captures || 0} capturas`}
                color={(ok.operations_week?.delay_minutes || 0) > 60 ? "text-red-600" : "text-amber-600"} />
          <KPI testid="op-kpi-attendance" icon={CalendarCheck}
                label={`Asistencia mes ${ok.shift_type === "night" ? "· Noche" : "· Día"}`}
                value={`${(ok.attendance_month?.D || 0) + (ok.attendance_month?.N || 0)}`}
                sub={`D ${ok.attendance_month?.D || 0} · N ${ok.attendance_month?.N || 0} · Ds ${ok.attendance_month?.Ds || 0} · A ${ok.attendance_month?.A || 0}`}
                color="text-blue-800" />
          <KPI testid="op-kpi-ranking" icon={Trophy}
                label="Ranking crew"
                value={ok.crew_ranking?.position ? `#${ok.crew_ranking.position}/${ok.crew_ranking.total}` : "—"}
                sub={ok.crew_ranking?.position ? "por horas capturadas" : "sin capturas esta semana"}
                color="text-purple-700" />
        </div>
      )}

      {/* Historial destajo — últimas 8 semanas (line chart) */}
      {ok?.destajo_history?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="op-destajo-history">
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <p className="overline-label">Historial destajo · últimas 8 semanas</p>
              <p className="text-xs text-slate-500">Bruto semanal en MXN</p>
            </div>
            <button onClick={onDownloadPdf} data-testid="op-download-weekly-pdf"
              className="text-xs font-semibold bg-blue-900 hover:bg-blue-800 text-white px-3 py-1.5 rounded gap-1 inline-flex items-center">
              <DownloadSimple size={14} weight="bold" /> PDF semanal
            </button>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={ok.destajo_history} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
              <XAxis dataKey="week_label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Line type="monotone" dataKey="gross" stroke="#059669" strokeWidth={2.5} dot={{ r: 4, strokeWidth: 2, fill: "#fff" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Accesos rápidos */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="op-quick-actions">
        <p className="overline-label mb-2">Accesos rápidos</p>
        <div className="flex flex-wrap gap-2">
          <QuickAction to="/mywork" testid="qa-my-work" icon={Plus} label="Capturar mi turno" tone="bg-blue-900 text-white hover:bg-blue-800" />
          <QuickAction to="/mywork#receipts" testid="qa-my-receipts" icon={Receipt} label="Mis recibos de destajo" tone="bg-emerald-700 text-white hover:bg-emerald-800" />
          <QuickAction to="/hse?tab=observations" testid="qa-report-obs" icon={ShieldCheck} label="Reportar observación" tone="bg-amber-600 text-white hover:bg-amber-700" />
          <QuickAction to="/hr" testid="qa-my-calendar" icon={CalendarCheck} label="Mi calendario" tone="bg-slate-800 text-white hover:bg-slate-900" />
        </div>
      </div>

      {/* Destajo — detalle de conceptos */}
      {ok?.destajo_week?.concepts?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="op-destajo-detail">
          <p className="overline-label px-6 pt-6">Detalle de destajo · esta semana</p>
          <table className="w-full data-table mt-2">
            <thead><tr><th>Concepto</th><th className="text-right">Cantidad</th><th>Unidad</th><th className="text-right">Pago (MXN)</th></tr></thead>
            <tbody>
              {ok.destajo_week.concepts.map((c, i) => (
                <tr key={i} data-testid={`op-destajo-row-${i}`}>
                  <td className="font-semibold">{c.label}</td>
                  <td className="text-right font-mono">{c.qty}</td>
                  <td className="text-xs text-slate-500">{c.unit}</td>
                  <td className="text-right font-mono font-bold text-emerald-700">{fmtMoney(c.cost)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300">
                <td colSpan={3} className="font-bold text-right">Total destajo</td>
                <td className="text-right font-heading font-black text-lg text-emerald-800">{fmtMoney(ok.destajo_week.gross)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Ranking crew */}
      {ok?.crew_ranking?.top?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="op-crew-ranking">
          <p className="overline-label px-6 pt-6">Top 5 · tu cuadrilla esta semana</p>
          <table className="w-full data-table mt-2">
            <thead><tr><th className="w-8">#</th><th>Operador</th><th className="text-right">Horas</th></tr></thead>
            <tbody>
              {ok.crew_ranking.top.map((r, i) => (
                <tr key={i} className={r.is_me ? "bg-purple-50 font-bold" : ""}
                    data-testid={`op-crew-rank-${i}`}>
                  <td className="font-mono">#{i + 1}</td>
                  <td>{r.name}{r.is_me && <span className="ml-2 text-[10px] uppercase tracking-widest text-purple-700 font-bold">(tú)</span>}</td>
                  <td className="text-right font-mono">{r.hours}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Últimos turnos capturados */}
      {m.recent_shifts?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <p className="overline-label px-6 pt-6">{t("dashOpRecentShifts")}</p>
          <table className="w-full data-table mt-2">
            <thead><tr><th>{t("date")}</th><th>{t("equipment")}</th><th>{t("area")}</th><th className="text-right">{t("hours")}</th></tr></thead>
            <tbody>
              {m.recent_shifts.slice(0, 8).map((s) => (
                <tr key={s.id}>
                  <td className="text-xs">{s.date}</td>
                  <td className="font-semibold">{s.equipment_type} · {s.equipment_code || ""}</td>
                  <td className="text-xs text-slate-500">{s.area || "—"}</td>
                  <td className="text-right font-mono text-xs">{s.hours || 0}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function QuickAction({ to, icon: Icon, label, tone, testid }) {
  return (
    <Link to={to} data-testid={testid}
       className={`inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition ${tone}`}>
      <Icon size={16} weight="bold" />
      <span>{label}</span>
      <ArrowRight size={13} weight="bold" className="opacity-70" />
    </Link>
  );
}

/* --- Supervisor: team activity + open items to review --- */
function SupervisorView({ t, data, mine, backlogAging, inspKpis }) {
  const m = mine || {};
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="sup-kpi-team-shifts" icon={ClockCounterClockwise} label={t("dashSupTeamShifts")} value={m.team_shifts_this_week || 0} sub={t("dashThisWeek")} />
        <KPI testid="sup-kpi-open-obs" icon={ShieldCheck} label={t("dashSupOpenObs")} value={data.safety?.open_incidents || 0} sub={t("dashSupOpenObsHint")} color="text-amber-500" />
        <KPI testid="sup-kpi-pending-hr" icon={HourglassMedium} label={t("dashSupPendingHr")} value={m.pending_hr_requests || 0} sub={t("dashSupPendingHrHint")} color="text-purple-600" />
        <KPI testid="sup-kpi-compliance" icon={CheckCircle} label={t("dashSupCompliance")} value={`${data.safety?.avg_compliance || 0}%`} color="text-emerald-600" />
      </div>

      {backlogAging && <BacklogAgingBanner data={backlogAging} />}
      {inspKpis && <InspectionsKpiBanner data={inspKpis} />}
      <SupervisorInlineActions />
      {m.recent_shifts?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <p className="overline-label px-6 pt-6">{t("dashSupTeamActivity")}</p>
          <table className="w-full data-table mt-2">
            <thead><tr><th>{t("date")}</th><th>{t("operator")}</th><th>{t("equipment")}</th><th>{t("area")}</th><th className="text-right">{t("hours")}</th></tr></thead>
            <tbody>
              {m.recent_shifts.slice(0, 10).map((s) => (
                <tr key={s.id}>
                  <td className="text-xs">{s.date}</td>
                  <td className="font-semibold">{s.operator_name || "—"}</td>
                  <td>{s.equipment_type} · {s.equipment_code || ""}</td>
                  <td className="text-xs text-slate-500">{s.area || "—"}</td>
                  <td className="text-right font-mono text-xs">{s.hours || 0}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* --- Backlog Aging Banner: OT urgentes abiertas >24h/>48h/>72h --- */
function BacklogAgingBanner({ data }) {
  const critical = (data.over_72h || 0) > 0;
  const warning = (data.over_24h || 0) > 0;
  const tone = critical
    ? "bg-red-50 border-red-300"
    : warning
    ? "bg-amber-50 border-amber-300"
    : "bg-emerald-50 border-emerald-300";
  const totalUrgent = data.total_urgent || 0;
  return (
    <div className={`border rounded-lg p-5 ${tone}`} data-testid="dash-backlog-aging">
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <Wrench size={22} weight="bold" className={critical ? "text-red-600" : warning ? "text-amber-600" : "text-emerald-600"} />
          <div>
            <p className="overline-label">Mantenimiento — envejecimiento del backlog urgente</p>
            <p className="font-heading font-black text-2xl mt-0.5 text-slate-900" data-testid="dash-backlog-total-urgent">
              {totalUrgent} <span className="text-sm font-semibold text-slate-500">OT urgentes abiertas</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 ml-auto">
          <AgeChip label=">24h" value={data.over_24h || 0} testid="dash-backlog-over-24h" tone={data.over_24h ? "amber" : "slate"} />
          <AgeChip label=">48h" value={data.over_48h || 0} testid="dash-backlog-over-48h" tone={data.over_48h ? "orange" : "slate"} />
          <AgeChip label=">72h" value={data.over_72h || 0} testid="dash-backlog-over-72h" tone={data.over_72h ? "red" : "slate"} />
        </div>
      </div>
      {data.oldest && (
        <p className="text-xs text-slate-600 mt-3" data-testid="dash-backlog-oldest">
          Más antigua: <b className="font-mono">{data.oldest.ot_number}</b> ·
          Equipo <b>{data.oldest.equipment_code}</b> (crit. {data.oldest.criticality}) ·
          <b> {Math.round(data.oldest.age_hours)}h</b> sin cerrar
          {data.oldest.motivo_real && <span className="text-slate-500 italic"> — “{data.oldest.motivo_real}”</span>}
        </p>
      )}
    </div>
  );
}

function AgeChip({ label, value, tone, testid }) {
  const tones = {
    slate: "bg-slate-100 text-slate-500",
    amber: "bg-amber-500 text-white",
    orange: "bg-orange-600 text-white",
    red: "bg-red-600 text-white",
  };
  return (
    <div className="text-center">
      <p className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${tones[tone]}`}>{label}</p>
      <p data-testid={testid} className={`font-heading font-black text-2xl mt-1 ${value > 0 ? "text-slate-900" : "text-slate-400"}`}>{value}</p>
    </div>
  );
}

/* --- Inspections KPI Banner: SLA compliance + top-3 inspectores --- */
function InspectionsKpiBanner({ data }) {
  const pct = data.sla_compliance_pct ?? 100;
  const tone = pct >= 90 ? "bg-emerald-50 border-emerald-300 text-emerald-700"
             : pct >= 75 ? "bg-amber-50 border-amber-300 text-amber-700"
             : "bg-red-50 border-red-300 text-red-700";
  return (
    <div className={`border rounded-lg p-5 ${tone}`} data-testid="dash-insp-kpi">
      <div className="flex flex-wrap items-start gap-6">
        <div className="flex items-center gap-3">
          <MagnifyingGlassPlus size={22} weight="bold" />
          <div>
            <p className="overline-label">Inspecciones — SLA compliance (últimos {data.window_days || 30}d)</p>
            <p className="font-heading font-black text-3xl mt-0.5" data-testid="dash-insp-compliance-pct">
              {pct}% <span className="text-sm font-semibold text-slate-500">de A+B cerradas a tiempo</span>
            </p>
            <p className="text-[11px] text-slate-600 mt-0.5">
              {data.total} hallazgos · {data.open?.A || 0}A abiertos · {data.open?.B || 0}B abiertos ·
              <b className="text-red-700"> {data.sla_breaches?.total || 0} vencidos</b>
            </p>
          </div>
        </div>
        {(data.top_inspectors || []).length > 0 && (
          <div className="ml-auto">
            <p className="overline-label text-slate-500 mb-1">Top 3 inspectores</p>
            <div className="flex gap-2">
              {data.top_inspectors.map((row, i) => (
                <div key={row.inspector} data-testid={`dash-insp-top-${i}`}
                  className="bg-white border border-slate-200 rounded px-3 py-1.5 text-xs shadow-sm">
                  <span className="text-[10px] font-bold text-slate-400 mr-1">#{i + 1}</span>
                  <span className="font-bold text-slate-900">{row.inspector}</span>
                  <span className="ml-2 text-slate-500">· {row.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

